import { DatesRangeValue } from '@mantine/dates';
import { createEffect, createEvent, Effect, EventCallable, sample } from 'effector';
import { saveAs } from 'file-saver';
import { jsPDF as JsPDF, jsPDFOptions } from 'jspdf';
import autoTable, { RowInput, UserOptions } from 'jspdf-autotable';
import * as XLSX from 'xlsx';

export const EXPORT_FORMAT = {
	xlsx: 'xlsx',
	pdf: 'pdf',
} as const satisfies Record<string, string>;

export type ExportFormat = (typeof EXPORT_FORMAT)[keyof typeof EXPORT_FORMAT];

export type SaveAsExcelParams = {
	data: (string | number)[][];
	sheetTitle: string;
	filenameTemplate: string;
	dates: DatesRangeValue<string>;
};

export type SaveAsPdfParams = {
	head: RowInput[];
	body: RowInput[];
	footer: RowInput[];
	filenameTemplate: string;
	dates: DatesRangeValue<string>;
	tableOptions?: Omit<UserOptions, 'head' | 'body' | 'footer'>;
	documentOptions?: jsPDFOptions;
};

export type ExportReportModel = {
	exportPdfClicked: EventCallable<void>;
	exportExcelClicked: EventCallable<void>;
	exportButtonClicked: EventCallable<ExportFormat>;
	saveAsExcelFx: Effect<SaveAsExcelParams, void>;
	saveAsPdfFx: Effect<SaveAsPdfParams, void>;
};

export const createExportReportModel = (): ExportReportModel => {
	const exportPdfClicked = createEvent();

	const exportExcelClicked = createEvent();

	const exportButtonClicked = createEvent<ExportFormat>();

	const saveAsExcelFx = createEffect<SaveAsExcelParams, void>(
		({ data, dates, filenameTemplate, sheetTitle }) => {
			const worksheet = XLSX.utils.aoa_to_sheet(data);
			const workbook = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(workbook, worksheet, sheetTitle);

			const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
			const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });

			const datePart = dates[0] && dates[1] ? `${dates[0]}_${dates[1]}` : '';

			const fileName = filenameTemplate.replace('%DATE%', datePart ? `-${datePart}` : '');

			saveAs(blob, fileName);
		},
	);

	const saveAsPdfFx = createEffect<SaveAsPdfParams, void>(
		({ head, body, footer, dates, filenameTemplate, tableOptions, documentOptions }) => {
			const doc = new JsPDF(documentOptions);

			autoTable(doc, {
				head,
				body,
				foot: footer,
				...tableOptions,
			});

			const datePart = dates[0] && dates[1] ? `${dates[0]}_${dates[1]}` : '';

			const fileName = filenameTemplate.replace('%DATE%', datePart ? `-${datePart}` : '');

			doc.save(fileName);
		},
	);

	sample({
		clock: [
			exportPdfClicked.map(() => EXPORT_FORMAT.pdf),
			exportExcelClicked.map(() => EXPORT_FORMAT.xlsx),
		],
		target: exportButtonClicked,
	});

	return {
		exportPdfClicked,
		exportExcelClicked,
		saveAsExcelFx,
		saveAsPdfFx,
		exportButtonClicked,
	};
};
